/*
 *
 * File: ChangeBookingScreen.js
 *
 * Purpose: Change Booking component file.
 *
 * Author:  Thomas Burgard (Software development)
 * Created: 03.04.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import axios from "axios";
import moment from "moment";
import {
  timeConvert,
  getCarCategoryData,
  getTariffs,
  getUserRegistrationData,
  roundFare,
} from "../utils/utils";
import * as constants from "../utils/constants";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import { getBookingData } from "../store/actions";
import { CHANGE_BOOKINGDATA } from "../api/actionCodes";
import { CANCEL_BOOKING } from "../api/actionCodes";
import { bookingDataValuesObj } from "../utils/dataObjects";

//--------------------------------------------------------------
//--- CSS styles -----------------------------------------------
//--------------------------------------------------------------
const routingResultBoxStyle = {
  border: "2px solid #75a74a",
  borderRadius: "5px",
  padding: "15px",
  paddingLeft: "13px",
  backgroundColor: "#f2ffe8",
};
//--- End Css styles -------------------------------------------

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const minDate = moment().toDate();
const bookingDataFormularValuesObj = bookingDataValuesObj;
const regEmail =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const fieldsErrorState = {
  errorEmail: true,
  errorTicketNumber: true,
  errorPickupTime: true,
  errorPickupDate: false,
  errorPassengerName: true,
};
let pickupFrom = "";
let waypoint1 = ""
let waypoint2 = "";
let waypoint3 = "";
let transferTo = "";
let payment = "";
let distance = "";
let carCategory = "";
let ticketNumber = "";
let pickupDate = "";
let pickupTime = "";
let timeDateChanged = false;
let emailConfirmBooking = "";
let statusOwner = "";
let premiumBooking = "";

// Server URL for React API
const URL_SERV = "https://api.drivepartner.com/multistop/";
const PHP_FILE_CHANGEBOOKINGDATA = "change_bookingdata.php";
const PHP_FILE_CANCELBOOKING = "cancel_booking.php";

let acceptancePeriodMissed = false;
let userRegistrationDataObj = null;
let emailError = false;
//---End component variable declarations -------------------------

/**
 * Implementation of DrivePartner change booking component.
 *
 * @param {*} props
 * @returns JSX-HTML-DOM
 */
const ChangeBookingScreen = (props) => {
  const userData = props.userData;
  const { t, i18n } = useTranslation("common");
  const selectedLanguage = useSelector((state) => state.languageReducer);

  //--------------------------------------------------------------
  //--- REDUX ----------------------------------------------------
  //--------------------------------------------------------------
  const dispatch = useDispatch();
  const bookingServerData = useSelector((state) => state.bookingReducer);

  //--------------------------------------------------------------
  //--- Refs -----------------------------------------------------
  //--------------------------------------------------------------   
  const refTarifSurcharge = useRef(0);
  const refOldTarifSurcharge = useRef(0);
  const refBasicPrice = useRef(0);
  const refOldPrice = useRef(0);

  //--------------------------------------------------------------
  //--- Component states -----------------------------------------
  //--------------------------------------------------------------
  const [price, setPrice] = useState(0);
  const [errorBackgroundEmail, setErrorBackgroundEmail] = useState(true);
  const [errorBackgroundTicketNumber, setErrorBackgroundTicketNumber] =
    useState(true);
  const [errorBackgroundPassengerName, setErrorBackgroundPassengerName] =
    useState(true);
  const [disableAddBtn, setDisableAddBtn] = useState(true);
  const [bookingDataFound, setBookingDataFound] = useState(false);
  const [bookingState, setBookingState] = useState({
    bookingFound: false,
    ticketNumber: "",
  });
  const [showErrorInfoBox, setShowErrorInfoBox] = useState("infobox_empty");
  const [validation, setValidation] = useState({
    pickupTime: null,
    pickupDate: null,
    passengerName: null,
    pax: 1,
    airport: null,
    comment: null,
    email: null,
  });
  //--- End states -----------------------------------------------

  userRegistrationDataObj = getUserRegistrationData(userData);

  //--------------------------------------------------------------
  //--- Will be called after "first" rendering! ------------------
  //--------------------------------------------------------------
  useEffect(() => {
    getTarifSurcharge();
  }, []);

  //--------------------------------------------------------------
  //--- Will be called after "each" rendering! -------------------
  //--------------------------------------------------------------
  useEffect(() => {
    if (timeDateChanged) {
      timeDateChanged = false;
    }
  });

  //--------------------------------------------------------------
  //--- Will be called only for "showErrorInfoBox" ---------------
  //--------------------------------------------------------------
  useEffect(() => {
    if (showErrorInfoBox !== "infobox_empty") {
      window.scrollTo(0, 2000);
    } else {
      window.scrollTo(0, 0);
    }
  }, [showErrorInfoBox]);

  //--------------------------------------------------------------
  //--- Will be called after "REDUX" state is changed ------------
  //--------------------------------------------------------------
  useEffect(() => {
    const tmpValidation = validation;

    if (bookingServerData.bookingData.BookingData !== "Wrong") {
      pickupFrom = bookingServerData.bookingData.BookingData["PickupFrom"];
      waypoint1 = bookingServerData.bookingData.BookingData["WAYPOINT1"];
      waypoint2 = bookingServerData.bookingData.BookingData["WAYPOINT2"];
      waypoint3 = bookingServerData.bookingData.BookingData["WAYPOINT3"];
      transferTo = bookingServerData.bookingData.BookingData["TransferTo"];
      pickupDate = bookingServerData.bookingData.BookingData["PickupDate"];
      pickupTime = bookingServerData.bookingData.BookingData["PickupTime"];
      payment = bookingServerData.bookingData.BookingData["PAYMENT"];
      distance = bookingServerData.bookingData.BookingData["DISTANCEKM"];      
      refOldTarifSurcharge.current = bookingServerData.bookingData.BookingData["TARIFSURCHARGE"];
      refOldPrice.current = bookingServerData.bookingData.BookingData["PRICE"];
      refBasicPrice.current = bookingServerData.bookingData.BookingData["BASIC_PRICE"];
      setPrice(bookingServerData.bookingData.BookingData["PRICE"]);
      
      if (
        bookingServerData.bookingData.BookingData[
          "CARCATEGORY_ALTERNATIVE_NAME"
        ] != "" &&
        bookingServerData.bookingData.BookingData[
          "CARCATEGORY_ALTERNATIVE_NAME"
        ] != null
      ) {
        carCategory =
          bookingServerData.bookingData.BookingData[
            "CARCATEGORY_ALTERNATIVE_NAME"
          ];
      } else {
        carCategory = bookingServerData.bookingData.BookingData["CARTYPE"];
      }

      emailConfirmBooking =
        bookingServerData.bookingData.BookingData["EmailConfirmBooking"];
      statusOwner = bookingServerData.bookingData.BookingData["StatusOwner"];

      // Set date object
      var dateArr = pickupDate.split("-");
      pickupDate = dateArr[1] + "/" + dateArr[2] + "/" + dateArr[0];
      pickupDate = new Date(pickupDate);

      // Set time date object with given time from database.
      // The date is not relevant for the time picker, but necessary.
      pickupTime = new Date("April 06, 2023 " + pickupTime);

      setBookingState({ bookingFound: true, ticketNumber: ticketNumber });

      setValidation({
        ...tmpValidation,
        ["pickupDate"]: pickupDate,
        ["pickupTime"]: pickupTime,
        ["passengerName"]:
          bookingServerData.bookingData.BookingData["PassengerName"],
        ["pax"]: bookingServerData.bookingData.BookingData["PAX"],
        ["airport"]: bookingServerData.bookingData.BookingData["Airport"],
        ["comment"]: bookingServerData.bookingData.BookingData["Comment"],
      });
    } else {
      setBookingState({ bookingFound: false, ticketNumber: ticketNumber });
    }
  }, [bookingServerData]);

  /**
   * Set background color for "ticket number" input validation.
   *
   * @returns color code
   */
  const setBgrColorTicketNumber = () => {
    if (bookingState.bookingFound) {
      return "#e0ffe3";
    } else {
      return "#FBF4F4FF";
    }
  };

  /**
   * Set background color for "email" input validation.
   *
   * @returns color code
   */
  const setBgrColorEmail = () => {
    if (errorBackgroundEmail) {
      return "#FBF4F4FF";
    } else {
      return "";
    }
  };

  /**
   * Submit form. Validate input fields
   * & send data to server after successfully validation.
   */
  const submitForm = () => {
    if (validateInputs()) {
      confirmDialog({
        position: "bottom",
        message: t("infotext.info-text-sendbooking"),
        header: t("infotext.info-text-sendbooking-dialogheader"),
        icon: "pi pi-question-circle",
        accept: () => sendBookingToServer(),
        reject: () => null,
      });
    } else {
      setShowErrorInfoBox("infobox_error_validation");

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);
    }
  };

  /**
   * Fetch booking data for sending booking data to server.
   *
   * @returns booking data object
   */
  const fetchBookingData = () => {
    return {
      actionCode: CHANGE_BOOKINGDATA,
      ticketNumber: bookingState.ticketNumber,
      userID: userRegistrationDataObj.userID,
      username: userRegistrationDataObj.username,
      language: selectedLanguage.language,
      pickupDate: bookingDataFormularValuesObj.pickupDate,
      pickupTime: bookingDataFormularValuesObj.pickupTime,
      passengerName: bookingDataFormularValuesObj.passengerName,
      pax: bookingDataFormularValuesObj.pax,
      airport: bookingDataFormularValuesObj.airport,
      comment: bookingDataFormularValuesObj.comment,
      email: bookingDataFormularValuesObj.email,
      emailConfirmBooking: emailConfirmBooking,
      statusOwner: statusOwner,
      tarif: userRegistrationDataObj.tariff,
      price: price,
      tarifSurcharge: refTarifSurcharge.current,
    };
  };

  /**
   * Fetch booking data for sending booking data to
   * server (in case of cancel booking).
   *
   * @returns booking data object
   */
  const fetchBookingDataForCancel = () => {
    return {
      actionCode: CANCEL_BOOKING,
      ticketNumber: bookingState.ticketNumber,
      userID: userRegistrationDataObj.userID,
      username: userRegistrationDataObj.username,
      language: selectedLanguage.language,
      email: bookingDataFormularValuesObj.email,
      emailConfirmBooking: emailConfirmBooking,
      statusOwner: statusOwner,
    };
  };

  /**
   * Send booking data now to DrivePartner-Server and
   * receive response from server.
   */
  const sendBookingToServer = () => {
    getFormValues();
    const bookingData = fetchBookingData();
    setShowErrorInfoBox("wait_circle");

    axios({
      method: "post",
      url: URL_SERV + PHP_FILE_CHANGEBOOKINGDATA,
      data: bookingData,
    }).then((response) => {
      if (response.data.Success) {
        setShowErrorInfoBox("infobox_success");
      } else {
        setShowErrorInfoBox("infobox_error_send_bookingdata");
      }

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);
    });
  };

  /**
   * Send booking data now to DrivePartner-Server and
   * receive response from server in case of cancel booking.
   */
  const sendBookingToServerCancel = () => {
    getFormValues();
    const bookingData = fetchBookingDataForCancel();
    setShowErrorInfoBox("wait_circle");

    axios({
      method: "post",
      url: URL_SERV + PHP_FILE_CANCELBOOKING,
      data: bookingData,
    }).then((response) => {
      if (response.data.Success) {
        setShowErrorInfoBox("infobox_success_cancel");
      } else {
        setShowErrorInfoBox("infobox_error_send_bookingdata");
      }

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);
    });
  };

  /**
   * Get all booking formular values and store into special
   * booking data formular values object.
   */
  const getFormValues = () => {
    bookingDataFormularValuesObj.pickupDate = moment(validation.pickupDate)
      .format("DD/MM/YYYY")
      .toString();
    bookingDataFormularValuesObj.pickupTime = moment(validation.pickupTime)
      .format("HH:mm")
      .toString();
    bookingDataFormularValuesObj.passengerName = validation.passengerName;
    bookingDataFormularValuesObj.pax = validation.pax;
    bookingDataFormularValuesObj.airport = validation.airport;
    bookingDataFormularValuesObj.comment = validation.comment;
    bookingDataFormularValuesObj.email = validation.email;
  };

  /**
   * Input change handler.
   *
   * @param {*} e event
   */
  const inputChangeHandler = (e) => {
    var inputValue = e.target.value;
    const nameField = e.target.name;

    emailError = false;

    if (nameField === "email") {
      if (!regEmail.test(inputValue)) {
        emailError = true;
      }
    } else if (nameField === "ticketNumber") {
      ticketNumber = inputValue;
      //----------------------------------------
      // Call Redux action to fetch booking data
      // from server with given ticket number
      //----------------------------------------
      dispatch(getBookingData(userRegistrationDataObj.userID, inputValue));
    }

    const tmpValidation = validation;
    setValidation({ ...tmpValidation, [nameField]: inputValue });
  };

  /**
   * Input blur (leave field) handler.
   *
   * @param {*} e event
   */
  const inputBlurHandler = (e) => {
    var inputValue = e.target.value;
    const idField = e.target.id;
    const idName = e.target.name;

    switch (idField) {
      case "email":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorEmail = true;
          setErrorBackgroundEmail(true);
        } else {
          fieldsErrorState.errorEmail = false;
          setErrorBackgroundEmail(false);
        }
        break;
      case "ticketNumber":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorTicketNumber = true;
          setErrorBackgroundTicketNumber(true);
        } else {
          fieldsErrorState.errorTicketNumber = false;
          setErrorBackgroundTicketNumber(false);
        }
        break;
      case "email":
        if (inputValue.trim() === "") {
          fieldsErrorState.errorEmail = true;
          setErrorBackgroundEmail(true);
        } else {
          fieldsErrorState.errorEmail = false;
          setErrorBackgroundEmail(false);
        }
        break;
    }
  };

  /**
   * Pickup date change handler.
   *
   * @param {*} event Date picker selection event
   */
  const changeDateHandler = (event) => {
    const tmpValidation = validation;

    timeDateChanged = true;
    checkAcceptancePeriod(event, false);
    setValidation({ ...tmpValidation, pickupDate: event.value });
    getTarifSurchargeChangeDate(event.value);
    calculatePricePickupDateChanged();
  };

  /**
   * Calculate prices only in case of premium user in case of
   * pickup date has changed.
   */
  const calculatePricePickupDateChanged = () => {
    let tmpPrice = 0;

    if (userRegistrationDataObj.tariff === 2) {
      if (refTarifSurcharge.current === 0 || refTarifSurcharge.current === "0") {
        //***********************************
        // Take price without tarif surcharge
        //***********************************
        if (refOldTarifSurcharge.current !== 0 && refOldTarifSurcharge.current !== "0") {  
          setPrice(refBasicPrice.current);
          
        } 
      } else {
        //********************************
        // Take price with tarif surcharge
        //********************************
        if (refOldTarifSurcharge.current !== 0 && refOldTarifSurcharge.current !== "0") {
          // Add new tarif surcharge to old price without tarif surcharge           
          tmpPrice = refBasicPrice.current * ((100 + parseInt(refTarifSurcharge.current)) / 100);           
          tmpPrice = tmpPrice | 0; 
          setPrice(tmpPrice);
        } else {
          tmpPrice = refBasicPrice.current * ((100 + parseInt(refTarifSurcharge.current)) / 100);
          tmpPrice = tmpPrice | 0;
          setPrice(tmpPrice);
        }
      }
    }
  };

  /**
   * Pickup time change handler.
   *
   * @param {*} event Time picker selection event
   */
  const changeTimeHandler = (event) => {
    const tmpValidation = validation;

    checkAcceptancePeriod(event, true);
    timeDateChanged = true;
    setValidation({ ...tmpValidation, pickupTime: event.value });
  };

  /**
   * Check "Annahmefrist" of user with input date & time.
   *
   * @param {*} event Date picker or time picker selection event
   * @param {*} eventIsTime Is event coming from time (true) or date (false) picker selection event
   */
  const checkAcceptancePeriod = (event, eventIsTime) => {
    let diffPeriod = null;
    let selectedOnlyDateStr = null;
    let selectedOnlyTimeStr = null;
    let actualDateStr = null;
    let actualTime = null;
    let selectedTime = null;
    let acceptancePeriod = userRegistrationDataObj.annahmefrist;

    if (
      acceptancePeriod === "0h" ||
      acceptancePeriod === null ||
      acceptancePeriod === ""
    ) {
      acceptancePeriodMissed = false;
    } else {
      acceptancePeriod = acceptancePeriod.charAt(0);
      acceptancePeriod = parseInt(acceptancePeriod) * 3600 * 1000;

      const actualElapsedTimeMillisec = moment().valueOf();

      if (eventIsTime) {
        selectedOnlyDateStr = moment(validation.pickupDate)
          .format("DD/MM/YYYY")
          .toString();
        selectedOnlyTimeStr = moment(event.value).format("HH:mm").toString(); // HH: 24 Std. format, hh: 12 Std. format
        selectedTime = moment(event.value).format("HH:mm");
      } else {
        selectedOnlyDateStr = moment(event.value)
          .format("DD/MM/YYYY")
          .toString();
        selectedOnlyTimeStr = moment(validation.pickupTime)
          .format("HH:mm")
          .toString(); // HH: 24 Std. format, hh: 12 Std. format
        selectedTime = moment(validation.pickupTime).format("HH:mm");
      }

      const newDateStr = moment(
        selectedOnlyDateStr + " " + selectedOnlyTimeStr,
        "DD/MM/YYYY HH:mm"
      ).valueOf();
      diffPeriod = Math.abs(newDateStr - actualElapsedTimeMillisec);

      if (diffPeriod >= acceptancePeriod) {
        acceptancePeriodMissed = false;
      } else {
        acceptancePeriodMissed = true;
      }

      // Check if time for today date is in the past
      actualDateStr = moment().format("DD/MM/YYYY").toString();

      if (selectedOnlyDateStr === actualDateStr) {
        actualTime = moment().format("HH:mm"); // Get actual time in HH:mm format

        if (selectedTime <= actualTime) {
          // Time is in the past!
          acceptancePeriodMissed = true;
        }
      }
    }
  };

  /**
   * Add tarif surcharges if configured and booking date
   * is in or between configured tarif surcharge date or date range.
   *
   */
  const getTarifSurcharge = () => {
    if (userData.tarifSurcharges === false) {
      return;
    }

    if (userData.tarifSurcharges.length > 0) {
      const date = moment(validation.pickupDate)
        .format("YYYY-MM-DD")
        .toString();

      for (let idx = 0; idx < userData.tarifSurcharges.length; idx++) {
        if (
          date >= userData.tarifSurcharges[idx]["STARTDATUM"] &&
          date <= userData.tarifSurcharges[idx]["ENDEDATUM"]
        ) {
          refTarifSurcharge.current = userData.tarifSurcharges[idx]["ZUSCHLAG"];
          break;
        } else {
          refTarifSurcharge.current = 0;
        }
      }
    }
  };

  /**
   * Add tarif surcharges if configured and booking date
   * is in or between configured tarif surcharge date or date range.
   *
   * This function is called, if pickup date is changed.
   *
   */
  const getTarifSurchargeChangeDate = (date) => {
    if (userData.tarifSurcharges === false) {
      return;
    }

    if (userData.tarifSurcharges.length > 0) {
      date = moment(date).format("YYYY-MM-DD").toString();

      for (let idx = 0; idx < userData.tarifSurcharges.length; idx++) {
        if (
          date >= userData.tarifSurcharges[idx]["STARTDATUM"] &&
          date <= userData.tarifSurcharges[idx]["ENDEDATUM"]
        ) {
          refTarifSurcharge.current = userData.tarifSurcharges[idx]["ZUSCHLAG"];
          break;
        } else {
          refTarifSurcharge.current = 0;
        }
      }
    }
  };

  /**
   * Show booking data table or not, depended of
   * server booking response data.
   *
   * @returns JSX-Code
   */
  const showBookingDataTable = () => {
    return bookingState.bookingFound ? (
      <>
        <h4>{t("screentext.screen-text-changeBooking-bookingdata")}</h4>
        <table style={{ borderSpacing: "0px" }}>
          <tr>
            <td style={{ color: "#3f98a6" }}>
              {t("screentext.screen-text-changeBooking-table-row1")}
            </td>
            <td>{pickupFrom}</td>
          </tr>
          {waypoint1 == null || waypoint1 === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-stoppoint-1")}
              </td>
              <td>{waypoint1}</td>
            </tr>
          )}
          {waypoint2 == null || waypoint2 === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-stoppoint-2")}
              </td>
              <td>{waypoint2}</td>
            </tr>
          )}
          {waypoint3 == null || waypoint3 === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-stoppoint-3")}
              </td>
              <td>{waypoint3}</td>
            </tr>
          )}
          <tr>
            <td style={{ color: "#3f98a6" }}>
              {t("screentext.screen-text-changeBooking-table-row2")}
            </td>
            <td>{transferTo}</td>
          </tr>
          {payment == null || payment === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-row3")}
              </td>
              <td>{payment}</td>
            </tr>
          )}
          {distance == null || distance === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-row4")}
              </td>
              <td>{distance} km</td>
            </tr>
          )}
          {price == null || price === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-row5")}
              </td>
              <td>{price} €</td>
            </tr>
          )}
          {carCategory == null || carCategory === "" ? null : (
            <tr>
              <td style={{ color: "#3f98a6" }}>
                {t("screentext.screen-text-changeBooking-table-row6")}
              </td>
              <td>{carCategory}</td>
            </tr>
          )}
        </table>
      </>
    ) : null;
  };

  /**
   * Show input fields or not, depended of
   * server booking response data.
   *
   * @returns JSX-Code
   */
  const showInputFields = () => {
    return bookingState.bookingFound ? (
      <>
        <div
          className="grid p-fluid"
          style={{ paddingLeft: "20px", paddingRight: "20px" }}
        >
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <Calendar
                id="pickupDate"
                name="pickupDate"
                minDate={minDate}
                value={validation.pickupDate}
                dateFormat="dd/mm/yy"
                onChange={(e) => changeDateHandler(e)}
                onBlur={(e) => inputBlurHandler(e)}
                showIcon
                locale="en"
              />
              <label htmlFor="pickupDate">
                {t("formular.placeholder-pickupdate")}
              </label>
            </span>
          </div>
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <Calendar
                id="pickupTime"
                name="pickupTime"
                value={validation.pickupTime}
                onChange={(e) => changeTimeHandler(e)}
                onBlur={(e) => inputBlurHandler(e)}
                timeOnly
                hourFormat="24"
                readOnlyInput
                showIcon
                stepMinute={5}
                icon="pi pi-clock"
              />
              <label htmlFor="pickupTime">
                {t("formular.placeholder-pickuptime")}
              </label>
            </span>
            {acceptancePeriodMissed ? (
              <span className="error-text">
                {t("errortext.error-acceptancePeriodMissed-part1") +
                  userRegistrationDataObj.annahmefrist +
                  t("errortext.error-acceptancePeriodMissed-part2")}
              </span>
            ) : !validation.pickupTimeError ? null : (
              <span className="error-text">
                {t("errortext.error-pickupTime-validation")}
              </span>
            )}
          </div>
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <InputNumber
                id="pax"
                name="pax"
                value={validation.pax}
                mode="decimal"
                onValueChange={(e) => inputChangeHandler(e)}
                showButtons
                min={1}
                max={60}
              />
              <label htmlFor="pax">{t("formular.placeholder-pax")}</label>
            </span>
          </div>
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <InputText
                id="passengerName"
                name="passengerName"
                value={validation.passengerName}
                onChange={(e) => inputChangeHandler(e)}
                onBlur={(e) => inputBlurHandler(e)}
                maxlength="100"
              />
              <label htmlFor="bookedFrom">
                {t("formular.placeholder-changebooking-passengername")}
              </label>
            </span>
          </div>
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <InputText
                id="airport"
                name="airport"
                maxlength="250"
                value={validation.airport}
                onChange={(e) => inputChangeHandler(e)}
              />
              <label htmlFor="airport">
                {t("formular.placeholder-airport")}
              </label>
            </span>
          </div>
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <InputText
                id="comment"
                name="comment"
                value={validation.comment}
                onChange={(e) => inputChangeHandler(e)}
                maxlength="250"
              />
              <label htmlFor="comment">
                {t("formular.placeholder-comment-examples")}
              </label>
            </span>
          </div>
          <div className="col-12 md:col-4">
            <span className="p-float-label">
              <InputText
                id="email"
                name="email"
                value={validation.email}
                keyfilter="email"
                onChange={(e) => inputChangeHandler(e)}
                onBlur={(e) => inputBlurHandler(e)}
                maxlength="250"
                style={{ backgroundColor: setBgrColorEmail() }}
              />
              <label htmlFor="email">
                {t("formular.placeholder-changebooking-email")}
              </label>
            </span>
            {emailError ? (
              <span className="error-text">
                {t("errortext.error-email-validation")}
              </span>
            ) : null}
          </div>
        </div>
      </>
    ) : null;
  };

  /**
   * Show buttons or not, depended of
   * server booking response data.
   *
   * @returns JSX-Code
   */
  const showButtons = () => {
    return bookingState.bookingFound ? (
      <>
        <div
          className="grid p-fluid"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            marginTop: "1px",
          }}
        >
          <div className="col-12">{showInfoBox()}</div>
        </div>
        <div
          className="grid p-fluid"
          style={{ paddingLeft: "20px", paddingRight: "20px" }}
        >
          <div className="col-12 md:col-4">
            <Button
              label={t("formular.btn-submit-booking")}
              icon="pi pi-send"
              onClick={() => submitForm()}
            />
          </div>
        </div>
        <div
          className="grid p-fluid"
          style={{ paddingLeft: "20px", paddingRight: "20px" }}
        >
          <div className="col-12 md:col-4">
            <Button
              label={t("formular.btn-cancel-booking")}
              icon="pi pi-trash"
              severity="danger"
              onClick={() => cancelBooking()}
            />
          </div>
        </div>
      </>
    ) : null;
  };

  /**
   * Show booking success info box or error info box.
   *
   * @returns JSX-Code for bokking success info box or error info box.
   */
  const showInfoBox = () => {
    if (showErrorInfoBox === "infobox_success") {
      return (
				<div className="success-box">
					<table>
						<tr>
							<td>
								<i
									className="pi pi-check-circle"
									style={{ fontSize: "2em", paddingRight: "20px" }}
								></i>
							</td>
							<td style={{ verticalAlign: "center" }}>
								<p>{t("successtext.success-store-Booking-text")}</p>
                <p style={{ fontSize: "1.3em", fontWeight: "bold" }}>
                  {t("successtext.success-store-Booking-text2")}
                </p>
							</td>
						</tr>
					</table>
				</div>
			);
    } else if (showErrorInfoBox === "infobox_success_cancel") {
      return (
        <div className="success-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-check-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("successtext.success-cancel-booking-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "infobox_error_validation") {
      return (
        <div className="error-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-exclamation-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("errortext.error-validation-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "infobox_error_send_bookingdata") {
      return (
        <div className="error-box">
          <table>
            <tr>
              <td>
                <i
                  className="pi pi-exclamation-circle"
                  style={{ fontSize: "2em", paddingRight: "20px" }}
                ></i>
              </td>
              <td style={{ verticalAlign: "center" }}>
                {t("errortext.error-sendbookingdata-text")}
              </td>
            </tr>
          </table>
        </div>
      );
    } else if (showErrorInfoBox === "wait_circle") {
      return <ProgressSpinner style={{ width: "60px", height: "60px" }} />;
    } else {
      return null;
    }
  };

  /**
   * Cancel booking.
   */
  const cancelBooking = () => {
    if (validateInputs()) {
      confirmDialog({
        position: "bottom",
        message: t("infotext.info-text-cancel-booking"),
        header: t("infotext.info-text-cancel-booking-dialogheader"),
        icon: "pi pi-question-circle",
        accept: () => sendBookingToServerCancel(),
        reject: () => null,
      });
    } else {
      setShowErrorInfoBox("infobox_error_validation");

      setTimeout(function () {
        setShowErrorInfoBox("infobox_empty");
      }, 6000);
    }
  };

  /**
   * Formular input fields validation.
   *
   * @returns true if validation is successfully, otherwise false
   */
  const validateInputs = () => {
    let validateSuccess = true;

    if (!validation.pickupDate) {
      validateSuccess = false;
    }

    if (!validation.pickupTime) {
      validateSuccess = false;
    }

    if (!validation.passengerName) {
      validateSuccess = false;
    }

    if (!validation.email) {
      validateSuccess = false;
    }

    if (acceptancePeriodMissed) {
      validateSuccess = false;
    }

    return validateSuccess;
  };

  /**
   * Reset all input fields.
   */
  const resetFields = () => {};

  //--------------------------------------------------------------
  //--- Returns JSX code for "ChangeBookingScreen ----------------
  //--------------------------------------------------------------
  return (
    <>
      <Header userData={userData} style={{ left: "0", right: "0" }} />
      <ConfirmDialog />
      <div
        id="jump-start"
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "15px" }}
      >
        <div className="col-12 md:col-12">
          <h2>{t("screentext.screen-text-changeBooking-header")}</h2>
          <p>{t("screentext.screen-text-changeBooking-header2")}</p>
        </div>
        <div style={{ marginBottom: "30px" }} />
        <div className="col-12 md:col-4">
          <label htmlFor="ticketNumber">
            {t("formular.placeholder-ticketNumber")}
          </label>
          <div style={{ marginBottom: "5px" }} />
          <InputText
            id="ticketNumber"
            name="ticketNumber"
            placeholder=" "
            onChange={(e) => inputChangeHandler(e)}
            style={{
              width: "100%",
              backgroundColor: setBgrColorTicketNumber(),
            }}
            maxlength="250"
            onBlur={(e) => inputBlurHandler(e)}
          />
        </div>
      </div>
      <div
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px" }}
      >
        <div className="col-12 md:col-12">{showBookingDataTable()}</div>
      </div>
      <div style={{ marginBottom: "30px" }} />
      {showInputFields()}
      <div style={{ marginBottom: "20px" }} />
      {showButtons()}
      <div style={{ paddingBottom: "100px" }} />
      <Footer userData={userData} />
    </>
  );
};

export default ChangeBookingScreen;
